<template>
  <!-- Page Header -->
  <PageHeader 
    title="Générer mon QR Code" 
    subtitle="Téléchargez votre QR Code pour votre page vendeur Efashion"
    class="mb-0" 
  />
  
  <div class="w-full px-2 sm:px-6 py-4">
    <!-- Section principale -->
    <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-8">
      <div class="text-center max-w-2xl mx-auto">
        <!-- Description -->
        <div class="mb-8">
          <h3 class="text-xl font-semibold text-gray-800 mb-4">
            Téléchargez votre QR Code
          </h3>
          <p class="text-gray-600 leading-relaxed">
            Téléchargez votre QR Code qui mène directement à votre page vendeur Efashion et donnez accès à tous vos clients en l'affichant dans votre boutique !
          </p>
        </div>
        <!-- Boutons d'action -->
        <div class="flex justify-center mb-10">
          <ActionButton
            label="Télécharger le QR Code"
            icon="fas fa-download"
            variant="primary"
            @click="downloadQRCode"
            :loading="isDownloading"
          />
        </div>
        <!-- QR Code Preview  -->
        <div class="mb-8" ref="qrcodeCanvas">
          <div class="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6">
            <div class="flex items-center justify-center">
              <div class="text-center">
                <h2 class="text-lg font-medium text-gray-700 mb-4">
                  Accédez à notre espace via Efashion Paris en scannant le qr code ci-dessous à l'aide de votre smartphone ou tablette.
                </h2>
                <div class="flex justify-center mb-4">
                  <QrcodeVue 
                    :value="qrCodeContent" 
                    :size="400"
                    class="border border-gray-200 rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { PageHeader, ActionButton } from '@/components'
import { useAuthStore } from '@/stores/auth'
import { useNotification } from '@/composables/useNotification'
import QrcodeVue from 'qrcode.vue'

// ===== STORES =====
const authStore = useAuthStore()

// ===== NOTIFICATIONS =====
const { error } = useNotification()

// ===== COMPOSANT VUE =====
// UI State
const isDownloading = ref(false)
const qrcodeCanvas = ref<HTMLElement>()

// ===== DONNÉES =====
// URL de base de l'API
const baseUrl = computed(() => {
  // Récupérer l'URL de base depuis les variables d'environnement
  return process.env.VUE_APP_API_BASE_URL
})

// ID du vendeur 
const sellerId = computed(() => {
  return authStore.user?.id_vendeur || authStore.user?.id
})

// Contenu du QR Code
const qrCodeContent = computed(() => {
  if (!sellerId.value) return ''
  return `${baseUrl.value}/api/seller/${sellerId.value}`
})

// ===== MÉTHODES UI =====
const downloadQRCode = async () => {
  try {
    isDownloading.value = true
    
    if (!sellerId.value) {
      throw new Error('ID vendeur non disponible')
    }
    
    if (!qrcodeCanvas.value) {
      throw new Error('Élément QR code non trouvé')
    }
    
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const html2canvas = require('html2canvas')
    
    const canvas = await html2canvas(qrcodeCanvas.value, {
      backgroundColor: '#ffffff',
      scale: 2
    })
    
    const link = document.createElement('a')
    link.download = 'qrcode-efashion-paris.png'
    link.href = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
  } catch (err) {
    console.error('Erreur lors du téléchargement:', err)
    error('Erreur lors du téléchargement du QR Code')
  } finally {
    isDownloading.value = false
  }
}

</script>

<style lang="scss" scoped>

</style>
